/**
 * TodayWork 桌面客户端的来源归因工具。
 *
 * 客户端主窗把 SaaS 页面嵌在 iframe 里跑，preload 会在每个同源框架加载前写入
 * sessionStorage['todaywork_client'] = '1'（每次导航重跑，框架内跳转不会丢）。
 * SaaS 侧据此给开通弹窗的来源串加 todaywork_ 前缀，让埋点和下单参数能区分出客户端的流量。
 *
 * 为什么不复用 desktopLib.isDesktop：它的取值是 `!!window.todayCameraAPI`，那是今日相机
 * 客户端注入的对象，TodayWork 客户端不注入。让它变成 true 会连带影响 20 多处分支，
 * 而 desktopLib 每个方法都在调 window.todayCameraAPI.*（401 时的 logoutSuccess、整套
 * 下载方法、clientInfo.version 等），所以这里单开一个标志，isDesktop 保持原样。
 */

/** 客户端来源串的统一前缀 */
const CLIENT_PREFIX = 'todaywork_'

/** 桌面客户端 preload 写入的标记键 */
const CLIENT_FLAG_KEY = 'todaywork_client'

/**
 * 当前页面是否跑在 TodayWork 桌面客户端里。
 *
 * 每次调用都现读 sessionStorage，不做模块级缓存——模块初始化的时机和 preload 写标记
 * 的时机没有先后保证，缓存会让先加载的模块永久拿到 false。
 *
 * @returns 客户端里为 true，网页版为 false
 *
 * @example
 * ```ts
 * isTodayWorkClient() // 客户端里 true；浏览器里 false
 * ```
 */
export function isTodayWorkClient(): boolean {
  try {
    return window.sessionStorage.getItem(CLIENT_FLAG_KEY) === '1'
  } catch {
    // 浏览器禁用存储或第三方 iframe 限制时，读 sessionStorage 会直接抛错。
    // 归因失败按网页版处理即可，绝不能让它把页面搞崩
    return false
  }
}

/**
 * 客户端里给付费来源串加上 todaywork_ 前缀，网页版原样返回。
 *
 * 空串保持空串（不要拼成裸 todaywork_，那会变成一个没有业务含义的来源值）；
 * 已带前缀的不重复拼——桌面端新增的卡点走 /embed/vip?source= 传进来时本身就带前缀。
 * 中文来源串保持中文，不做转写。
 *
 * @param sourceDetail - 业务传入的原始来源串，可能为空
 * @returns 客户端里带前缀的来源串，其余情况原样返回
 *
 * @example
 * ```ts
 * // 客户端里
 * withClientPrefix('aiFormFiller')            // 'todaywork_aiFormFiller'
 * withClientPrefix('web端整改任务条数限制')    // 'todaywork_web端整改任务条数限制'
 * withClientPrefix('todaywork_我的积分弹窗升级会员') // 原样返回，不重复拼
 * withClientPrefix('')                        // ''
 * // 网页版
 * withClientPrefix('aiFormFiller')            // 'aiFormFiller'
 * ```
 */
export function withClientPrefix(sourceDetail: string): string {
  if (!sourceDetail) return sourceDetail
  if (!isTodayWorkClient()) return sourceDetail
  if (sourceDetail.startsWith(CLIENT_PREFIX)) return sourceDetail

  return CLIENT_PREFIX + sourceDetail
}

/**
 * 去掉来源串上的 todaywork_ 前缀。
 *
 * 供按来源值查表、按来源值做功能分支的下游使用：加前缀后这些地方收到的值不再等于
 * 原来的枚举，不剥前缀会静默失效——定制文案退化成通用版本，该进的功能分支进不去。
 * 上报给神策和后端的值必须保持带前缀，不要在那些地方顺手剥掉。
 *
 * @param sourceDetail - 可能带前缀的来源串
 * @returns 去掉前缀后的来源串；没有前缀时原样返回
 *
 * @example
 * ```ts
 * stripClientPrefix('todaywork_downloadOnce') // 'downloadOnce'
 * stripClientPrefix('downloadOnce')           // 'downloadOnce'
 * stripClientPrefix('')                       // ''
 * ```
 */
export function stripClientPrefix(sourceDetail: string): string {
  if (!sourceDetail) return sourceDetail
  if (!sourceDetail.startsWith(CLIENT_PREFIX)) return sourceDetail

  return sourceDetail.slice(CLIENT_PREFIX.length)
}
